import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useUserSetupStore } from '@sib/auth';
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Input,
  VisuallyHidden,
} from '@sib/ui';
import { SearchIcon, XIcon } from '@spendbrain/icons';
import { OrganizationListItem } from './OrganizationListItem';
import { OrganizationListLoader } from './OrganizationListLoader';
import {
  OrganizationSwitchDialogProps,
  useOrganizationSwitch,
} from './OrganizationSwitchDialog.utils';

export const OrganizationSwitchDialog = ({
  open,
  onClose,
}: OrganizationSwitchDialogProps) => {
  const {
    onSearch,
    onSubmit,
    defaultOrganization,
    search,
    filteredOrganizations,
    onDialogClose,
    selectedOrganization,
    setSelectedOrganization,
    infiniteList,
    isLoading,
  } = useOrganizationSwitch({ onClose, open });
  const { t } = useTranslation();
  const organizationId = useUserSetupStore(state => state.organizationId);

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onDialogClose()}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-[90vw] flex-col items-center gap-7 px-6 py-6 sm:max-w-[80vw] sm:px-12">
        <VisuallyHidden.Root>
          <DialogTitle>{t('organizationSwitchDialog.switch')}</DialogTitle>
        </VisuallyHidden.Root>
        <h5 className="text-xl font-semibold">
          {t('components.organizationSwitchDialog.title')}
        </h5>

        <div className="flex flex-col items-center">
          <p className="text-sm">
            {t('components.organizationSwitchDialog.selectedAccount')}
          </p>
          <p className="text-center text-sm font-semibold">
            {defaultOrganization?.name || ''}
          </p>
        </div>
        <Card className="flex w-full flex-col items-stretch gap-2 p-[14px]">
          <p className="text-sm leading-[18px]">{t('search')}</p>
          <Input
            placeholder={t('filters.searchPlaceholder')}
            endAdornment={
              search ? (
                <XIcon className="size-5" onClick={() => onSearch('')} />
              ) : (
                <SearchIcon className="size-5" />
              )
            }
            value={search}
            onChange={e => onSearch(e.target.value)}
          />
        </Card>
        <Card className="flex w-full flex-col">
          <div
            className="flex max-h-[200px] min-h-[200px] w-full flex-[1_1] flex-col overflow-auto"
            id="organization-list-container"
          >
            {isLoading ? (
              <OrganizationListLoader size="md" />
            ) : filteredOrganizations.length ? (
              <InfiniteScroll
                dataLength={infiniteList.dataLength}
                next={infiniteList.fetchNextPage}
                hasMore={infiniteList.hasMore}
                loader={<OrganizationListLoader />}
                scrollableTarget="organization-list-container"
              >
                {filteredOrganizations.map(organization => (
                  <OrganizationListItem
                    key={organization.id}
                    isSelected={selectedOrganization?.id === organization.id}
                    organization={organization}
                    onSelect={setSelectedOrganization}
                  />
                ))}
              </InfiniteScroll>
            ) : (
              <div className="p-3">
                <p className="text-sm">
                  {t('components.organizationSwitchDialog.noResults')}
                </p>
              </div>
            )}
          </div>
          <div className="border-muted flex justify-end border-t p-1.5">
            <p className="font-sm leading-[18px]">
              {t('partOfTotal', {
                part: filteredOrganizations.length,
                total: infiniteList.totalRows,
              })}
            </p>
          </div>
        </Card>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            color="primary"
            onClick={onDialogClose}
          >
            {t('cancel')}
          </Button>
          <Button
            size="sm"
            color="primary"
            onClick={onSubmit}
            disabled={organizationId === selectedOrganization?.id}
          >
            {t('confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
